import {
    isValidElement
} from "react";
import type {
    AnimatedIconNode,
    AnimatedIconTag
} from "./type";
import {
    ANIMATED_ICON_TAGS
} from "./constant";
import {
    getAnimatedIconNodeLength
} from "./path";
import type {
    LucideIcon
} from "lucide-react-native";

const NODE_CACHE = new WeakMap<object, Array<AnimatedIconNode> | null>();

const isTag = (tag: string): tag is AnimatedIconTag => {
    return ANIMATED_ICON_TAGS.some((item) => item === tag);
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const getRawNodes = (icon: LucideIcon): unknown => {
    const renderer: unknown = Reflect.get(icon, "render");

    if(typeof renderer !== "function") {
        return null;
    }

    const element: unknown = Reflect.apply(renderer, undefined, [
        {},
        null
    ]);

    if(!isValidElement(element) || !isRecord(element.props)) {
        return null;
    }

    return element.props.iconNode;
};

const getNode = (entry: unknown): AnimatedIconNode | null => {
    if(!Array.isArray(entry) || typeof entry[0] !== "string" || !isTag(entry[0]) || !isRecord(entry[1])) {
        return null;
    }

    const attributes: Record<string, string | number> = {};

    Object.entries(entry[1]).forEach(([
        key,
        value
    ]) => {
        if(key !== "key" && (typeof value === "string" || typeof value === "number")) {
            attributes[key] = value;
        }
    });

    const tag = entry[0];

    return {
        length: getAnimatedIconNodeLength({
            attributes,
            tag
        }),
        attributes,
        tag
    };
};

export const getAnimatedIconNodes = ({
    icon
}: {
    icon: LucideIcon;
}): Array<AnimatedIconNode> | null => {
    if(NODE_CACHE.has(icon)) {
        return NODE_CACHE.get(icon) ?? null;
    }

    const raw = getRawNodes(icon);

    const nodes = Array.isArray(raw) ? raw.map(getNode) : [];

    const result = nodes.length && nodes.every((node) => node !== null) ? nodes : null;

    NODE_CACHE.set(icon, result);

    return result;
};

export const getAnimatedIconName = ({
    icon
}: {
    icon: LucideIcon;
}): string => {
    return (icon.displayName ?? "").replace(/([a-z])([A-Z0-9])/g, "$1-$2").toLowerCase();
};
